import React from 'react';
import Footer from 'shared/components/footer/Footer';
import {
  ButtonLink,
  FunnelPage,
  FunnelSection,
} from 'shared/components/funnel/FunnelUI';
import './service-detail.css';

function ActionLinks({ actions = [], className = '' }) {
  if (!actions.length) {
    return null;
  }

  return (
    <div className={`service-detail-actions ${className}`.trim()}>
      {actions.map((action) => (
        <ButtonLink
          key={`${action.label}-${action.to || action.href}`}
          to={action.to}
          href={action.href}
          variant={action.variant || 'primary'}
        >
          {action.label}
        </ButtonLink>
      ))}
    </div>
  );
}

function DetailMetric({ value, label, note }) {
  return (
    <div className="service-detail-metric">
      <strong>{value}</strong>
      <span>{label}</span>
      {note && <small>{note}</small>}
    </div>
  );
}

function DetailMedia({ media }) {
  if (!media) {
    return null;
  }

  const { src, alt, type = 'image', label } = media;

  return (
    <figure className="service-detail-media-frame">
      {type === 'video' ? (
        <video src={src} muted loop autoPlay playsInline />
      ) : (
        <img src={src} alt={alt || ''} />
      )}
      {label && <figcaption>{label}</figcaption>}
    </figure>
  );
}

function DetailVisual({ visual = {} }) {
  const rows = visual.rows || [];
  const chips = visual.chips || [];

  return (
    <div className="service-detail-visual" aria-label={visual.label || 'Service preview'}>
      <div className="service-detail-orbit" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="service-detail-console">
        <div className="service-detail-console-header">
          <span>{visual.kicker || 'CRYPTOXNFT'}</span>
          {visual.badge && <strong>{visual.badge}</strong>}
        </div>
        <div className="service-detail-console-rows">
          {rows.map((row) => (
            <div className="service-detail-console-row" key={`${row.label}-${row.title}`}>
              <span>{row.label}</span>
              <strong>{row.title}</strong>
              <small>{row.meta}</small>
            </div>
          ))}
        </div>
        {chips.length > 0 && (
          <div className="service-detail-console-chips" aria-label="Service attributes">
            {chips.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </div>
        )}
      </div>
      <DetailMedia media={visual.media} />
    </div>
  );
}

function SectionIntro({ eyebrow, title, copy, align = 'center' }) {
  return (
    <div className={`service-detail-intro service-detail-intro--${align}`.trim()}>
      {eyebrow && <p className="funnel-eyebrow">{eyebrow}</p>}
      <h2>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

function FeatureCard({ feature, index }) {
  return (
    <article className="service-detail-feature-card">
      <span className="service-detail-card-index">{String(index + 1).padStart(2, '0')}</span>
      <p>{feature.label}</p>
      <h3>{feature.title}</h3>
      <span>{feature.copy}</span>
    </article>
  );
}

function StepCard({ step, index }) {
  return (
    <article className="service-detail-step-card">
      <span>{String(index + 1).padStart(2, '0')}</span>
      <div>
        <h3>{step.title}</h3>
        <p>{step.copy}</p>
      </div>
    </article>
  );
}

function ProofGrid({ items = [] }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="service-detail-proof-grid">
      {items.map((item) => (
        <DetailMedia key={`${item.src}-${item.label}`} media={item} />
      ))}
    </div>
  );
}

function ServiceDetailPage({
  pageClass,
  eyebrow,
  title,
  copy,
  actions = [],
  metrics = [],
  visual,
  featuresIntro,
  features = [],
  flowIntro,
  steps = [],
  proofIntro,
  proofItems = [],
  resource,
  notice,
  finalCta,
}) {
  return (
    <FunnelPage className={`service-detail-page ${pageClass || ''}`.trim()}>
      <FunnelSection tone="hero" className="service-detail-hero-section">
        <div className="service-detail-hero-grid">
          <div className="service-detail-hero-copy">
            {eyebrow && <p className="funnel-eyebrow">{eyebrow}</p>}
            <h1>{title}</h1>
            <p>{copy}</p>
            <ActionLinks actions={actions} />
            {metrics.length > 0 && (
              <div className="service-detail-metrics">
                {metrics.map((metric) => (
                  <DetailMetric key={`${metric.value}-${metric.label}`} {...metric} />
                ))}
              </div>
            )}
          </div>
          <DetailVisual visual={visual} />
        </div>
      </FunnelSection>

      <FunnelSection className="service-detail-feature-section">
        {featuresIntro && <SectionIntro {...featuresIntro} />}
        {features.length > 0 && (
          <div className="service-detail-feature-grid">
            {features.map((feature, index) => (
              <FeatureCard key={`${feature.label}-${feature.title}`} feature={feature} index={index} />
            ))}
          </div>
        )}
      </FunnelSection>

      <FunnelSection tone="muted" className="service-detail-flow-section">
        <div className="service-detail-flow-layout">
          {flowIntro && <SectionIntro {...flowIntro} align="start" />}
          <div className="service-detail-step-list">
            {steps.map((step, index) => (
              <StepCard key={`${step.title}-${index}`} step={step} index={index} />
            ))}
          </div>
        </div>
      </FunnelSection>

      {proofIntro && (
        <FunnelSection className="service-detail-proof-section">
          <SectionIntro {...proofIntro} />
          <ProofGrid items={proofItems} />
          {notice && <p className="service-detail-notice">{notice}</p>}
        </FunnelSection>
      )}

      {resource && (
        <FunnelSection tone="panel" className="service-detail-resource-section">
          <SectionIntro {...resource.intro} />
          <div className="service-detail-resource-shell">
            {resource.content}
          </div>
        </FunnelSection>
      )}

      {finalCta && (
        <FunnelSection className="service-detail-final-section">
          <div className="service-detail-final-panel">
            <div className="service-detail-final-copy">
              {finalCta.eyebrow && <p className="funnel-eyebrow">{finalCta.eyebrow}</p>}
              <h2>{finalCta.title}</h2>
              <p>{finalCta.copy}</p>
            </div>
            <ActionLinks actions={finalCta.actions} className="service-detail-final-actions" />
          </div>
        </FunnelSection>
      )}

      <Footer />
    </FunnelPage>
  );
}

export default ServiceDetailPage;
